<template>
    <v-container fluid class="my-0 py-0 px-0 page-shell">
        <v-row class="my-0 py-0 mx-0" align="center" justify="center">
            <v-col cols="12" align="center" class="px-0 py-0">
                <v-carousel
                    cycle
                    class="news-carousel"
                    :height="carouselHeight"
                    hide-delimiter-background
                    hide-delimiters
                    show-arrows
                >
                    <v-carousel-item v-for="(article, index) in carouselArticles" :key="index" @click="goToArticle(article)">
                        <v-card
                            class="clickable elevation-0 news-carousel-card"
                        >
                            <div
                                class="news-carousel-watermark"
                                :style="{ backgroundImage: `url('${getImage(article)}')` }"
                            />
                            <v-img
                                :src="getImage(article)"
                                class="news-carousel-image"
                                contain
                                position="center center"
                            />
                            <div class="news-carousel-caption custom-orange">
                                <v-card-title class="text-h6 white--text news-carousel-title" v-text="article.title" />
                                <v-card-subtitle class="white--text news-carousel-subtitle"> {{ getDate(article) }} </v-card-subtitle>
                            </div>
                        </v-card>
                    </v-carousel-item>
                </v-carousel>
                <sponsors-section />
            </v-col>
        </v-row>
        <v-row ref="newsListStart" class="mx-0">
            <v-col v-for="(article, index) in articles" :key="index" cols="12" md="6">
                <article-card :article="article" />
            </v-col>
            <template v-if="isLoading && articles.length === 0">
                <v-col
                    v-for="index in initialSkeletonCount"
                    :key="`initial-skeleton-${index}`"
                    cols="12"
                    md="6"
                >
                    <v-skeleton-loader class="news-card-skeleton" type="image, article" />
                </v-col>
            </template>
        </v-row>
        <v-row class="mx-0 mb-4" justify="center">
            <v-col cols="12" class="d-flex justify-center">
                <v-pagination
                    :value="page"
                    :length="Math.max(totalPages, 1)"
                    :total-visible="$vuetify.breakpoint.mobile ? 5 : 8"
                    class="rounded-xl"
                    @input="handlePageChange"
                />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import ArticleCard from '@/components/ArticleCard'
    import articleService from '@/services/article'
    import SponsorsSection from '@/components/SponsorsSection'

    export default {
        components: { ArticleCard, SponsorsSection },
        data() {
            return {
                page: 1,
                size: 10,
                totalPages: 0,
                initialSkeletonCount: 6,
                carouselArticles: [],
                articles: [],
                isLoading: false
            }
        },
        computed: {
            carouselHeight() {
                if (this.$vuetify.breakpoint.xs) {
                    return '74vh'
                }

                if (this.$vuetify.breakpoint.sm) {
                    return '80vh'
                }

                return '86vh'
            }
        },
        created() {
            this.fetchArticles()
        },
        methods: {
            goToArticle(article) {
                this.$router.push({ name: 'Article', params: { id: article.id } })
            },
            handlePageChange(nextPage) {
                if (nextPage === this.page || this.isLoading) {
                    return
                }

                this.page = nextPage
                this.fetchArticles()
                    .finally(() => {
                        this.scrollToNewsListStart()
                    })
            },
            fetchArticles() {
                if (this.isLoading) {
                    return Promise.resolve()
                }

                this.isLoading = true

                return articleService.getArticles(this.page, this.size)
                    .then(resp => {
                        const nextArticles = Array.isArray(resp.data.data) ? resp.data.data : []

                        if (this.page === 1 && this.carouselArticles.length === 0) {
                            this.carouselArticles = nextArticles.slice(0, 5)
                        }

                        this.articles = nextArticles
                        this.totalPages = Number(resp.data.last_page) || 0
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },
            scrollToNewsListStart() {
                this.$nextTick(() => {
                    const target = this.$refs.newsListStart
                    const targetElement = Array.isArray(target) ? target[0] : target

                    if (!targetElement || !targetElement.getBoundingClientRect) {
                        return
                    }

                    const offset = this.$vuetify.breakpoint.mobile ? 70 : 110
                    const top = targetElement.getBoundingClientRect().top + window.pageYOffset - offset

                    window.scrollTo({
                        top: Math.max(top, 0),
                        left: 0,
                        behavior: 'smooth'
                    })
                })
            },
            getImage(article) {
                if (article.image) {
                    return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${article.image.imageName}`
                } else {
                    return require('@/assets/main_logo.png')
                }
            },
            getDate(article) {
                const date = new Date(article.articleDate)
                return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
            }
        }
    }
</script>

<style scoped>

.news-carousel {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
}

.news-carousel::v-deep .v-window {
    order: 1;
    height: 99% !important;
}

.news-carousel::v-deep .v-window__container {
    height: 99% !important;
}

.news-carousel::v-deep .v-carousel__item {
    height: 99% !important;
}

.news-carousel::v-deep .v-carousel__controls {
    display: none !important;
}

.news-carousel-card {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: transparent;
}

.news-carousel-watermark {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    opacity: 0.3;
    filter: blur(8px);
    transform: scale(1.06);
    z-index: 0;
    pointer-events: none;
}

.news-carousel-image {
    position: relative;
    z-index: 1;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    background-color: transparent;
}

.news-carousel-caption {
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    width: 100%;
    padding: 8px 12px;
}

.news-carousel-title {
    text-align: left;
    word-break: break-word;
    line-height: 1.3;
    padding: 4px 0;
    text-transform: none !important;
}

.news-carousel-subtitle {
    text-align: end;
    word-break: break-word;
    padding: 0 0 2px;
    text-transform: none !important;
}

.news-card-skeleton {
    min-height: 430px;
}

@media only screen and (max-width: 1264px) {
    .news-carousel {
        max-width: 100%;
    }
}

@media only screen and (max-width: 600px) {
    .news-carousel-title {
        font-size: 1rem;
    }
}
</style>
